import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthenticatedAxios } from "../../utils/api";
import Header from "../../components/Header";
import LoadingLogo from "../../components/LoadingLogo";
import {
  MessageSquare,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Info,
  Calendar,
  Building,
  User,
  ArrowLeft,
} from "lucide-react";

const SurveyResponses = () => {
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterInterested, setFilterInterested] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  const fetchResponses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCompany) params.append("company_id", filterCompany);
      if (filterRole && filterRole !== "all")
        params.append("user_role", filterRole);
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const axios = getAuthenticatedAxios();
      const response = await axios.get(
        `/api/v1/admin/survey-responses?${params.toString()}`,
      );
      if (response.data.success) {
        setResponses(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch survey responses:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange.end, dateRange.start, filterCompany, filterRole]);

  const fetchStatistics = useCallback(async () => {
    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.get("/api/v1/admin/survey-statistics");
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.get("/api/v1/admin/companies");
      if (response.data.success) {
        setCompanies(response.data.data.companies || []);
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
    fetchCompanies();
  }, [fetchCompanies, fetchStatistics]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const handleExport = () => {
    // Convert to CSV
    const headers = [
      "Date",
      "User",
      "Role",
      "Company",
      "Interested",
      "Use Case",
      "Needed Searches",
      "Additional Feedback",
    ];
    const csvData = responses.map((r) => [
      new Date(r.CreatedAt).toLocaleDateString(),
      r.UserEmail || "Unknown",
      r.UserRole || r.UserRoleFromDB || "user",
      r.CompanyName || "N/A",
      r.Interested ? "Yes" : "No",
      r.UseCase || "",
      r.NeededSearches || "",
      r.AdditionalFeedback || "",
    ]);

    const csv = [
      headers.join(","),
      ...csvData.map((row) =>
        row.map((cell) => `"${cell.toString().replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `survey-responses-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Apply client-side filtering for interest (since it's not filtered on backend)
  const filteredResponses = responses.filter((r) => {
    if (filterInterested === "yes" && !r.Interested) return false;
    if (filterInterested === "no" && r.Interested) return false;
    return true;
  });

  const getUseCaseColor = (useCase) => {
    const colors = {
      research: "bg-purple-100 text-purple-800",
      clinical: "bg-blue-100 text-blue-800",
      education: "bg-green-100 text-green-800",
      personal: "bg-yellow-100 text-yellow-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[useCase] || colors.other;
  };

  const getNeededSearchesColor = (needed) => {
    const colors = {
      "100-200": "bg-green-100 text-green-800",
      "200-500": "bg-yellow-100 text-yellow-800",
      "500-1000": "bg-orange-100 text-orange-800",
      "1000+": "bg-red-100 text-red-800",
      unlimited: "bg-purple-100 text-purple-800",
    };
    return colors[needed] || "bg-gray-100 text-gray-800";
  };

  const getRoleBadgeColor = (role) => {
    if (role === "admin") return "bg-blue-100 text-blue-800";
    if (role === "demo") return "bg-amber-100 text-amber-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingLogo size="md" message="Loading survey responses..." />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Survey Responses
                </h1>
                <p className="text-gray-600 mt-1">
                  User feedback about unlimited search plans
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
                <button
                  onClick={() => {
                    fetchResponses();
                    fetchStatistics();
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </button>
                <button
                  onClick={() => navigate("/admin")}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </button>
              </div>
            </div>

            {/* Statistics */}
            {statistics && (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                  <div className="text-sm text-gray-600">Total Responses</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {statistics.total_responses}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                  <div className="text-sm text-gray-600">Interested</div>
                  <div className="text-2xl font-bold text-green-600">
                    {statistics.interested_count} (
                    {statistics.interested_percentage}%)
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                  <div className="text-sm text-gray-600">
                    Most Common Use Case
                  </div>
                  <div className="text-lg font-bold text-blue-600">
                    {Object.entries(
                      statistics.use_case_distribution || {},
                    ).sort(([, a], [, b]) => b - a)[0]?.[0] || "N/A"}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                  <div className="text-sm text-gray-600">Most Requested</div>
                  <div className="text-lg font-bold text-purple-600">
                    {Object.entries(
                      statistics.needed_searches_distribution || {},
                    ).sort(([, a], [, b]) => b - a)[0]?.[0] || "N/A"}
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Filter
                  </label>
                  <select
                    value={filterCompany}
                    onChange={(e) => setFilterCompany(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Role Filter
                  </label>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="demo">Demo</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interest Filter
                  </label>
                  <select
                    value={filterInterested}
                    onChange={(e) => setFilterInterested(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Responses</option>
                    <option value="yes">Interested Only</option>
                    <option value="no">Not Interested Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, start: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, end: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Responses List */}
            <div className="space-y-4">
              {filteredResponses.map((response) => (
                <div
                  key={response.SurveyId}
                  className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`p-2 rounded-full ${
                          response.Interested
                            ? "bg-green-100 text-green-600"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {response.Interested ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <XCircle className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {response.UserEmail || "Unknown User"}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(response.UserRole || response.UserRoleFromDB || "user")}`}
                          >
                            {(
                              response.UserRole ||
                              response.UserRoleFromDB ||
                              "user"
                            ).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <Building className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {response.CompanyName || "No Company"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {new Date(response.CreatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-600">Use Case:</span>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUseCaseColor(
                          response.UseCase,
                        )}`}
                      >
                        {response.UseCase || "Not specified"}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">
                        Needed Searches:
                      </span>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNeededSearchesColor(
                          response.NeededSearches,
                        )}`}
                      >
                        {response.NeededSearches || "Not specified"}
                      </span>
                    </div>
                  </div>

                  {response.AdditionalFeedback && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Additional Feedback:
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {response.AdditionalFeedback}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredResponses.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No survey responses found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyResponses;
